<template>
  <v-form
    ref="entityDeleteForm"
    key=""
    v-model="isValidForm"
    :disabled="isDeleting"
    @submit.prevent=""
  >
    <v-card
      :max-width="maxWidth"
      class="pt-3 px-4"
      flat
      :outlined="true"
      :color="cardColor"
    >
      <v-card-title style="word-break: break-word">
        <h1 class="text-h5" :class="titleDarkness">
          {{ deleteButtonCaptionsVerb }}
        </h1>
      </v-card-title>
      <v-card-text>
        <div class="text-h6 font-weight-medium ml-4">
          {{ deleteButtonCaptionsVerb }} {{ entityNameTitleOverride }}?
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <!--
          `variant="elevated"` is required, not decoration: Vuetify 3's
          VCardActions does `provideDefaults({ VBtn: { slim: true, variant:
          'text' } })`, so every button inside it renders FLAT and transparent.
          Vuetify 2's v-card__actions injected nothing, so these were filled.
          Without this the Cancel button loses its grey fill and the Delete
          button its purple fill (it rendered as purple text on transparent).
        -->
        <v-btn
          ref="btn"
          variant="elevated"
          :color="cancelButtonColor"
          :disabled="isDeleting"
          :rounded="true"
          class="mt-8 mb-5 mr-3 text-high-emphasis"
          :width="cancelButtonWidth"
          @click="cancel"
        >
          Cancel
        </v-btn>
        <v-btn
          ref="btn"
          variant="elevated"
          :disabled="isDeleting"
          :loading="isDeleting"
          :rounded="true"
          :width="calculatedDeleteButtonWidth"
          class="mt-8 mb-5"
          :color="deleteButtonColor"
          type="submit.prevent"
          @click="verifyOrDoDelete"
        >
          <v-icon v-if="isVerifyingDelete" left> mdi-alert </v-icon>
          {{ deleteButtonLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { isDarkTheme } from '~/cynqueModules/nuxtAccessors'
import type { TEntityData } from '~/types/models'

const mn = '[components/EntityDeleteForm.vue] |'

export default defineComponent({
  props: {
    entityName: {
      type: String as PropType<string | null>,
      default: null,
    },
    entityNameTitleOverride: {
      type: String as PropType<string | null>,
      default: null,
    },
    entityData: {
      type: [Object, Array] as PropType<TEntityData>,
      default: undefined,
    },
    deleteButtonCaptionsVerbOverride: {
      type: String as PropType<string | null>,
      default: null,
    },
    maxWidth: {
      type: Number as PropType<number>,
      required: true,
    },
    isDeleting: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    deleteButtonWidth: {
      type: Number as PropType<number>,
      default: 150,
    },
    cancelButtonWidth: {
      type: Number as PropType<number>,
      default: 150,
    },
    doTintAndDarkenOutlineForLightTheme: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  emits: ['cancel', 'delete'],
  data: () => ({
    dialogTitlePrefix: '{Prefix not set}',
    isValidForm: true,
    isVerifyingDelete: false,
  }),
  computed: {
    // Bridge's Cancel button took the framework's default button fill, which is
    // theme-aware: #F5F5F5 in light but #272727 (with white text) in dark. The V3
    // port hard-coded `color="grey-lighten-4"`, so in dark mode the button stayed
    // near-white and read as the loudest thing in the dialog. Measured on Bridge:
    // light rgb(245,245,245), dark rgb(39,39,39). V3 derives the label colour from
    // the background, so naming the fill restores the white label too.
    cancelButtonColor(): string {
      return isDarkTheme() ? '#272727' : 'grey-lighten-4'
    },
    cardColor(): string | undefined {
      return isDarkTheme() ? '#333' : undefined
    },
    titleDarkness(): string {
      // V3: fold the color into the shade token (primary lighten-1). V2's standalone
      // `text--lighten-1` modifier no longer works apart from the color class.
      return isDarkTheme()
        ? 'text-primary-lighten-1'
        : 'font-weight-medium text-primary-lighten-1'
    },
    title(): string {
      return this.entityNameTitleOverride
        ? this.entityNameTitleOverride
        : this.entityNameCapitalized
    },
    entityNameCapitalized(): string {
      return this.entityName
        ? this.entityName.charAt(0).toUpperCase() + this.entityName.slice(1)
        : ''
    },
    calculatedDeleteButtonWidth(): number {
      return this.isVerifyingDelete ? 200 : this.deleteButtonWidth
    },
    deleteButtonLabel(): string {
      return this.isVerifyingDelete
        ? `Really ${this.deleteButtonCaptionsVerb}?`
        : this.deleteButtonCaptionsVerb
    },
    deleteButtonColor(): string {
      return this.isVerifyingDelete ? 'error' : 'primary'
    },
    deleteButtonCaptionsVerb(): string {
      return this.deleteButtonCaptionsVerbOverride
        ? this.deleteButtonCaptionsVerbOverride
        : 'Delete'
    },
    deleteButtonCaptionsVerbUpperCase(): string {
      return this.deleteButtonCaptionsVerb.toUpperCase()
    },
  },
  methods: {
    verifyOrDoDelete() {
      const fn = `${mn} delete |`
      if (!this.isVerifyingDelete) {
        console.debug(
          `${fn} BUTTON PUSHED: VERIFY ${this.deleteButtonCaptionsVerbUpperCase}`
        )
        this.isVerifyingDelete = true
        return
      }
      console.debug(
        `${fn} BUTTON PUSHED: REALLY ${this.deleteButtonCaptionsVerbUpperCase}`
      )
      this.$emit('delete', this.entityData)
    },
    cancel() {
      console.debug(`${mn} cancel | BUTTON PUSHED: CANCEL`)
      this.isVerifyingDelete = false
      this.$emit('cancel')
    },
  },
})
</script>

<style scoped>
.v-divider {
  border-color: rgb(var(--v-theme-primary-lighten-1));
}
.v-theme--dark.v-divider {
  border-color: rgb(var(--v-theme-primary-darken-1));
}
</style>
